import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Send, Eye, X, Image, CheckSquare, Users, RefreshCw, Upload, Trash2, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import Header from '@/components/Header';
import SendingProgressModal from '@/components/SendingProgressModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GreetingsEmail = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [recipientCount, setRecipientCount] = useState({ total: 0, breakdown: {} });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressJobId, setProgressJobId] = useState('');
  
  // Sender accounts state
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  
  const [formData, setFormData] = useState({
    magazine: 'Aabkari Times',
    subject: 'Season\'s Greetings!',
    image_url: '',
    greeting_message: 'May this festive season bring joy, prosperity, and good health to you and your family.',
    recipient_types: [],
    sender_email: '',
    sender_name: ''
  });

  const recipientOptions = [
    { value: 'all_members', label: 'All Members', group: 'Members' },
    { value: 'active_members', label: 'Active Members', group: 'Members' },
    { value: 'inactive_members', label: 'Inactive Members', group: 'Members' },
    { value: 'complimentary_members', label: 'Complimentary Members', group: 'Members' },
    { value: 'all_advertisers', label: 'All Advertisers', group: 'Advertisers' },
    { value: 'active_advertisers', label: 'Active Advertisers', group: 'Advertisers' },
    { value: 'inactive_advertisers', label: 'Inactive Advertisers', group: 'Advertisers' },
    { value: 'complimentary_advertisers', label: 'Complimentary Advertisers', group: 'Advertisers' }
  ];

  // Fetch sender accounts when magazine changes
  useEffect(() => {
    fetchSenderAccounts();
  }, [formData.magazine]);

  useEffect(() => {
    if (formData.recipient_types.length > 0) {
      fetchRecipientCount();
    } else {
      setRecipientCount({ total: 0, breakdown: {} });
    }
  }, [formData.recipient_types, formData.magazine]);

  const fetchSenderAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/email/sender-accounts/${formData.magazine}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSenderAccounts(response.data || []);
      
      // Set default sender if available
      const defaultSender = response.data?.find(a => a.is_default);
      if (defaultSender && !selectedSender) {
        setSelectedSender(defaultSender);
        setFormData(prev => ({
          ...prev,
          sender_email: defaultSender.email,
          sender_name: defaultSender.display_name
        }));
      }
    } catch (error) {
      console.error('Error fetching sender accounts:', error);
    }
  };

  const handleSenderChange = (accountId) => {
    const account = senderAccounts.find(a => a.id === accountId);
    if (account) {
      setSelectedSender(account);
      setFormData(prev => ({
        ...prev,
        sender_email: account.email,
        sender_name: account.display_name
      }));
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/greetings`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const fileUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, image_url: fileUrl }));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const fetchRecipientCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/greetings/count-recipients`, {
        magazine: formData.magazine,
        recipient_types: formData.recipient_types
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipientCount(response.data);
    } catch (error) {
      console.error('Error fetching recipient count:', error);
    }
  };

  const handleRecipientToggle = (value) => {
    setFormData(prev => ({
      ...prev,
      recipient_types: prev.recipient_types.includes(value)
        ? prev.recipient_types.filter(t => t !== value)
        : [...prev.recipient_types, value]
    }));
  };

  const handlePreview = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/greetings/preview`, {
        magazine: formData.magazine,
        image_url: formData.image_url,
        greeting_message: formData.greeting_message
      });
      setPreviewHtml(response.data.html);
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!formData.subject) {
      toast.error('Please enter a subject');
      return;
    }
    if (formData.recipient_types.length === 0) {
      toast.error('Please select at least one recipient group');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      
      // Start progress tracking
      const progressResponse = await axios.post(`${API}/email/progress/start`, {
        total: recipientCount.total,
        email_type: 'greetings'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const jobId = progressResponse.data.job_id;
      setProgressJobId(jobId);
      setShowProgressModal(true);
      
      // Send with job_id for tracking
      const response = await axios.post(`${API}/greetings/send`, {
        ...formData,
        job_id: jobId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Greetings sent to ${response.data.sent_count} recipient(s)!`);
      setShowPreview(false);
    } catch (error) {
      console.error('Error sending greetings:', error);
      toast.error(error.response?.data?.detail || 'Failed to send greetings');
      setShowProgressModal(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-amber-50 py-8">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="text-4xl font-bold" style={{ color: '#f59e0b' }}>
              Greetings Email
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Left - Form */}
          <div className="col-span-2 space-y-6">
            {/* Magazine & Subject */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="font-semibold mb-4 flex items-center">
                <Mail className="mr-2" size={18} />
                Email Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Magazine</Label>
                  <select
                    value={formData.magazine}
                    onChange={(e) => setFormData({ ...formData, magazine: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="Aabkari Times">Aabkari Times</option>
                    <option value="Sugar Times">Sugar Times</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Subject Line *</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="e.g., Happy Diwali: Best wishes to you & your family!"
                  />
                </div>
              </div>
              
              {/* Sender Email Selection */}
              <div className="mt-4 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Label className="font-semibold text-indigo-800">Send From:</Label>
                    <select
                      value={selectedSender?.id || ''}
                      onChange={(e) => handleSenderChange(e.target.value)}
                      className="h-10 w-72 rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm"
                    >
                      {senderAccounts.length === 0 ? (
                        <option value="">No sender accounts configured</option>
                      ) : (
                        senderAccounts.map(account => (
                          <option key={account.id} value={account.id}>
                            {account.display_name} ({account.email})
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  {senderAccounts.length === 0 && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => navigate('/email-settings')}
                      className="text-indigo-700 border-indigo-300"
                    >
                      <Settings className="mr-1" size={14} />
                      Configure
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Image & Message */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="font-semibold mb-4 flex items-center">
                <Image className="mr-2" size={18} />
                Greeting Content
              </h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Greeting Image</Label>
                  
                  {/* File Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-amber-400 transition-colors">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    
                    {formData.image_url ? (
                      <div className="space-y-3">
                        <img 
                          src={formData.image_url} 
                          alt="Greeting Preview" 
                          className="max-h-48 mx-auto rounded-lg shadow"
                        />
                        <div className="flex justify-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                          >
                            <Upload className="mr-1" size={14} />
                            Change Image
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({ ...formData, image_url: '' })}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-1" size={14} />
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Upload className="mx-auto text-gray-400 mb-2" size={40} />
                        <p className="text-sm text-gray-600 mb-2">
                          Click to upload greeting image
                        </p>
                        <Button
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                        >
                          {uploading ? 'Uploading...' : 'Choose File'}
                        </Button>
                        <p className="text-xs text-gray-500 mt-2">
                          Recommended: 600x800px (JPEG, PNG, GIF, WebP, Max 10MB)
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-xs text-gray-500">Or paste URL directly:</div>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/diwali-greeting.jpg"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Greeting Message</Label>
                  <Textarea
                    value={formData.greeting_message}
                    onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
                    placeholder="Your greeting message..."
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Recipients */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h2 className="font-semibold mb-4 flex items-center">
                <Users className="mr-2" size={18} />
                Select Recipients *
              </h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-blue-700 mb-3">Members</h3>
                  <div className="space-y-2">
                    {recipientOptions.filter(o => o.group === 'Members').map(option => (
                      <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.recipient_types.includes(option.value)}
                          onChange={() => handleRecipientToggle(option.value)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{option.label}</span>
                        {recipientCount.breakdown[option.value] > 0 && (
                          <span className="text-xs text-gray-500">({recipientCount.breakdown[option.value]})</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-green-700 mb-3">Advertisers</h3>
                  <div className="space-y-2">
                    {recipientOptions.filter(o => o.group === 'Advertisers').map(option => (
                      <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.recipient_types.includes(option.value)}
                          onChange={() => handleRecipientToggle(option.value)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm">{option.label}</span>
                        {recipientCount.breakdown[option.value] > 0 && (
                          <span className="text-xs text-gray-500">({recipientCount.breakdown[option.value]})</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Summary & Actions */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border sticky top-6">
              <h2 className="font-semibold mb-4">Summary</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Magazine:</span>
                  <span className="font-medium">{formData.magazine}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Recipients:</span>
                  <span className="font-medium text-amber-600">{recipientCount.total} email(s)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Image:</span>
                  <span className={formData.image_url ? 'text-green-600' : 'text-gray-400'}>
                    {formData.image_url ? '✓ Added' : 'Not added'}
                  </span>
                </div>
              </div>
              
              <div className="mt-6 space-y-3">
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={handlePreview}
                  disabled={loading}
                >
                  <Eye className="mr-2" size={16} />
                  {loading ? 'Loading...' : 'Preview Email'}
                </Button>
                <Button
                  className="w-full"
                  style={{ backgroundColor: '#f59e0b' }}
                  onClick={handleSend}
                  disabled={sending || recipientCount.total === 0}
                >
                  <Send className="mr-2" size={16} />
                  {sending ? 'Sending...' : `Send to ${recipientCount.total} Recipients`}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
              <div className="bg-amber-500 text-white p-4 flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center">
                  <Eye className="mr-2" size={24} />
                  Email Preview
                </h2>
                <button onClick={() => setShowPreview(false)} className="text-white hover:text-gray-200">
                  <X size={24} />
                </button>
              </div>
              <div className="overflow-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
                <div className="p-4 bg-gray-100">
                  <div className="bg-white rounded-lg shadow mx-auto" style={{ maxWidth: '600px' }}>
                    <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                  </div>
                </div>
              </div>
              <div className="p-4 border-t flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setShowPreview(false)}>
                  Close
                </Button>
                <Button
                  style={{ backgroundColor: '#f59e0b' }}
                  onClick={handleSend}
                  disabled={sending || recipientCount.total === 0}
                >
                  <Send className="mr-2" size={16} />
                  {sending ? 'Sending...' : `Confirm & Send`}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Sending Progress Modal */}
        <SendingProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          jobId={progressJobId}
          totalRecipients={recipientCount.total}
          emailType="Greetings"
        />
      </div>
    </div>
  );
};

export default GreetingsEmail;
